// packages/game-shell/src/sim/fixtures/dsl/FixtureDsl.ts

import type { SpinResponse } from "@slot/client-engine";
import type { ActionPhaseName, RoundEvent, RoundEventContent, RoundAction, RoundSnapshot, GambleOfferDetails } from "@slot/client-engine";
import { encodeGridSnapshot } from "./GridEncoding";

function nowIso(): string {
  return new Date().toISOString();
}

function requireNonEmptyString(name: string, v: unknown): string {
  if (typeof v !== "string" || v.length === 0) throw new Error(`${name} must be a non-empty string.`);
  return v;
}

type FeatureStateLike = {
  featureIdentifier: string;
  featureCountRemaining: string;
  featureAccumulatedWinMinor?: string;
  featurePhase: "enter" | "update" | "exit";
};

export class EventBuilder {
  private readonly eventTypeName: string;
  private readonly eventSequenceNumber: number;

  private reelSetIdentifier?: string;
  private encodedGridSnapshot?: string;
  private anticipatedColumns?: number[];
  private anticipatedCells?: number[];
  private featureState?: FeatureStateLike;

  private accumulatedWinAmountAfterEvent?: string;
  private betAmountAtEvent?: string;
  private betLinesAtEvent?: number;

  private orderedActions: RoundAction[] = [];

  public constructor(eventTypeName: string, eventSequenceNumber: number) {
    this.eventTypeName = requireNonEmptyString("eventTypeName", eventTypeName);
    this.eventSequenceNumber = eventSequenceNumber;
  }

  public reelSet(id: string): this {
    this.reelSetIdentifier = requireNonEmptyString("reelSetIdentifier", id);
    return this;
  }

  /** Provide rows: [ ["A","K",...], ... ] */
  public gridRows(rows: string[][]): this {
    this.encodedGridSnapshot = encodeGridSnapshot(rows);
    return this;
  }

  /** Provide the already-encoded string */
  public gridEncoded(encoded: string): this {
    this.encodedGridSnapshot = requireNonEmptyString("encodedGridSnapshot", encoded);
    return this;
  }

  /** Server-flagged anticipation columns (0-based) for this reveal (reels mechanic). */
  public anticipate(columns: number[]): this {
    this.anticipatedColumns = [...columns];
    return this;
  }

  /** Server-flagged anticipation cells (0-based, row-major) for this reveal (hold mechanic, decision 54). */
  public anticipateCells(cells: number[]): this {
    this.anticipatedCells = [...cells];
    return this;
  }

  public featureStateSet(feature: FeatureStateLike): this {
    this.featureState = feature;
    return this;
  }

  /** The win after this event (minor units). Required on the round's last event: the round's total, which decides the settlement (decision 26). */
  public accumulatedWinAfter(amountMinor: string): this {
    this.accumulatedWinAmountAfterEvent = requireNonEmptyString("accumulatedWinAmountAfterEvent", amountMinor);
    return this;
  }

  /** The bet this event was played at (backend stamps it on every event). */
  public betAmount(amountMinor: string): this {
    this.betAmountAtEvent = requireNonEmptyString("betAmountAtEvent", amountMinor);
    return this;
  }

  /** The lines that bet covered, for a title with lines (decision 56): `betAmount` is their total. */
  public betLines(lines: number): this {
    if (!Number.isInteger(lines) || lines < 1) throw new Error(`EventBuilder.betLines: a whole number of at least 1, got ${lines}`);
    this.betLinesAtEvent = lines;
    return this;
  }

  public action(actionTypeName: string, payload: Record<string, unknown>, opts?: {
    /** One of the engine's phases (until 2026-09-26 any string; review finding 59). */
    actionPhaseName?: ActionPhaseName;
    isSkippable?: boolean;
    debugTag?: string;
  }): this {
    const a: RoundAction = {
      actionTypeName,
      actionPayload: payload,
      ...(opts?.actionPhaseName ? { actionPhaseName: opts.actionPhaseName } : {}),
      ...(typeof opts?.isSkippable === "boolean" ? { isSkippable: opts.isSkippable } : {}),
      ...(opts?.debugTag ? { debugTag: opts.debugTag } : {}),
    } as RoundAction;

    this.orderedActions.push(a);
    return this;
  }

  /** The event without its round id: `SpinFixtureBuilder.build` stamps that on every event, as the backend does. */
  public build(): Omit<RoundEvent, "roundIdentifier"> {
    const content: RoundEventContent = {
      orderedActions: this.orderedActions,
    };

    if (this.reelSetIdentifier) content.reelSetIdentifier = this.reelSetIdentifier;
    if (this.encodedGridSnapshot) content.encodedGridSnapshot = this.encodedGridSnapshot;
    if (this.anticipatedColumns) content.anticipatedColumns = this.anticipatedColumns;
    if (this.anticipatedCells) content.anticipatedCells = this.anticipatedCells;
    if (this.featureState) content.featureState = this.featureState;

    const ev: Omit<RoundEvent, "roundIdentifier"> = {
      eventTypeName: this.eventTypeName,
      eventSequenceNumber: this.eventSequenceNumber,
      eventContent: content,
    };

    if (this.accumulatedWinAmountAfterEvent) {
      ev.accumulatedWinAmountAfterEvent = this.accumulatedWinAmountAfterEvent;
    }

    if (this.betAmountAtEvent) {
      ev.betAmountAtEvent = this.betAmountAtEvent;
    }

    if (this.betLinesAtEvent !== undefined) {
      ev.betLinesAtEvent = this.betLinesAtEvent;
    }

    return ev;
  }
}

export class SpinFixtureBuilder {
  private readonly roundIdentifier: string;

  private schemaVersion: string = "1";
  private serverTimeIsoString: string = nowIso();

  private currencyCode: string = "EUR";
  private balanceAmount: string = "0";

  private roundStatus: string = "completed";
  private possibleClientActions: string[] = [];
  private gambleOfferDetailsValue?: GambleOfferDetails;

  private events: Omit<RoundEvent, "roundIdentifier">[] = [];

  public constructor(roundIdentifier: string) {
    this.roundIdentifier = requireNonEmptyString("roundIdentifier", roundIdentifier);
  }

  public schema(v: string): this {
    this.schemaVersion = requireNonEmptyString("schemaVersion", v);
    return this;
  }

  public time(iso: string): this {
    this.serverTimeIsoString = requireNonEmptyString("serverTimeIsoString", iso);
    return this;
  }

  public balance(currencyCode: string, balanceAmountMinor: string): this {
    this.currencyCode = requireNonEmptyString("currencyCode", currencyCode);
    this.balanceAmount = requireNonEmptyString("balanceAmount", balanceAmountMinor);
    return this;
  }

  public clientActions(actions: string[]): this {
    this.possibleClientActions = actions;
    return this;
  }

  /**
   * Declare the server-authoritative gamble offer for this round.
   * When set, the ScriptedRoundProvider will not inject a default offer.
   * Should be used together with `.clientActions([..., "gamble"])`.
   * Its currentWinAmountMinor is what the first pick stakes: author the
   * round's win; "0" is no offer (decision 29).
   */
  public gambleOffer(details: GambleOfferDetails): this {
    this.gambleOfferDetailsValue = details;
    return this;
  }

  public status(roundStatus: string): this {
    this.roundStatus = requireNonEmptyString("roundStatus", roundStatus);
    return this;
  }

  public event(eventTypeName: string, eventSequenceNumber: number, build: (e: EventBuilder) => void): this {
    const eb = new EventBuilder(eventTypeName, eventSequenceNumber);
    build(eb);
    this.events.push(eb.build());
    return this;
  }

  public build(): SpinResponse {
    const roundSnapshot: RoundSnapshot = {
      roundIdentifier: this.roundIdentifier,
      roundStatus: this.roundStatus,
      possibleClientActions: this.possibleClientActions,
      // Backend shape: every event also carries the round id.
      orderedEvents: this.events.map((e) => Object.assign({ roundIdentifier: this.roundIdentifier }, e)),
    };

    if (this.gambleOfferDetailsValue) {
      roundSnapshot.gambleOfferDetails = this.gambleOfferDetailsValue;
    }

    const spin: SpinResponse = {
      schemaVersion: this.schemaVersion,
      serverTimeIsoString: this.serverTimeIsoString,
      accountBalanceSnapshot: {
        currencyCode: this.currencyCode,
        balanceAmount: this.balanceAmount,
      },
      roundSnapshot,
    };

    return spin;
  }
}

/**
 * Entry point
 */
export function spinFixture(roundIdentifier: string): SpinFixtureBuilder {
  return new SpinFixtureBuilder(roundIdentifier);
}